import { useState, useEffect, useCallback } from 'react';
import { memberProfileAPI } from '../services/api';
import { MemberProfile } from '../types';

interface UseActiveMemberProfilesOptions {
  /** Filter to only active members. Default: true */
  activeOnly?: boolean;
  /** Sort profiles by full name (firstName lastName). Default: true */
  sortByName?: boolean;
  /** Automatically load profiles on mount. Default: true */
  autoLoad?: boolean;
  /** Remove duplicate profiles by ID. Default: false */
  deduplicate?: boolean;
}

interface UseActiveMemberProfilesReturn {
  /** Array of member profiles */
  profiles: MemberProfile[];
  /** Loading state */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Function to manually refetch profiles */
  refetch: () => Promise<MemberProfile[]>;
}

/**
 * Custom hook to fetch and filter member profiles
 * Consolidates common pattern of fetching active members and sorting by name
 * 
 * @param options Configuration options
 * @returns Object containing profiles, loading state, error, and refetch function
 * 
 * @example
 * // Get active members sorted by name (most common use case)
 * const { profiles, loading, error } = useActiveMemberProfiles();
 * 
 * @example
 * // Get all profiles without filtering
 * const { profiles } = useActiveMemberProfiles({ activeOnly: false, sortByName: false });
 * 
 * @example
 * // Manual loading (useful for dialogs)
 * const { profiles, loading, refetch } = useActiveMemberProfiles({ autoLoad: false });
 * // Later: await refetch();
 * 
 * @example
 * // With deduplication
 * const { profiles } = useActiveMemberProfiles({ deduplicate: true });
 */
export const useActiveMemberProfiles = (
  options: UseActiveMemberProfilesOptions = {}
): UseActiveMemberProfilesReturn => {
  const {
    activeOnly = true,
    sortByName = true,
    autoLoad = true,
    deduplicate = false
  } = options;

  const [profiles, setProfiles] = useState<MemberProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await memberProfileAPI.getAllMemberProfiles();
      let processedProfiles = response.data || [];

      // Filter active members
      if (activeOnly) {
        processedProfiles = processedProfiles.filter(
          (p: MemberProfile) => p.status === 'active'
        );
      }

      // Deduplicate by ID if needed
      if (deduplicate) {
        const seen = new Set<string | number>();
        processedProfiles = processedProfiles.filter((p: MemberProfile) => {
          if (seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
      }

      // Sort by full name
      if (sortByName) {
        processedProfiles.sort((a: MemberProfile, b: MemberProfile) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }

      setProfiles(processedProfiles);
      return processedProfiles;
    } catch (err: any) {
      console.error('Error fetching member profiles:', err);
      const errorMessage = err.response?.data?.message || 'Failed to fetch member profiles';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [activeOnly, sortByName, deduplicate]);

  useEffect(() => {
    if (autoLoad) {
      fetchProfiles();
    }
  }, [autoLoad, fetchProfiles]);

  return {
    profiles,
    loading,
    error,
    refetch: fetchProfiles
  };
};
